import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getDesgloseCompleto, descargarDesgloseExport } from "../api"
import { formatCurrency, formatNumber, formatCantidad, formatPercent, formatFecha } from "../helpers"

const PAGE_SIZE = 100

const TIPOS_COMP = [
  { v: "01", l: "01 · Factura de venta" },
  { v: "91", l: "91 · Nota crédito" },
  { v: "92", l: "92 · Nota débito" },
  { v: "03", l: "03 · Nota crédito" },
  { v: "04", l: "04 · Doc. soporte" },
]

const ORDER_OPTIONS = [
  { v: "fecha", l: "Fecha" },
  { v: "importe", l: "Importe" },
  { v: "cantidad", l: "Cantidad" },
  { v: "producto", l: "Producto" },
]

const StatPill = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 180px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 14px",
  }}>
    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 6 }}>
      <i className={`mdi ${icon}`} style={{ color: accent }} />
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
)

StatPill.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const CopiarCufeMini = ({ cufe }) => {
  const [copiado, setCopiado] = useState(false)
  if (!cufe) return null
  const copiar = async (e) => {
    e.stopPropagation()
    try { await navigator.clipboard.writeText(cufe); setCopiado(true); setTimeout(() => setCopiado(false), 1500) }
    catch { /* noop */ }
  }
  return (
    <button type="button" onClick={copiar}
      title={copiado ? "Copiado" : "Copiar CUFE completo"}
      style={{
        background: "none", border: "none", padding: 2, cursor: "pointer",
        color: copiado ? "#10b981" : "#94a3b8",
        display: "inline-flex", alignItems: "center",
      }}>
      <i className={`mdi mdi-${copiado ? "check" : "content-copy"}`} style={{ fontSize: 13 }} />
    </button>
  )
}

CopiarCufeMini.propTypes = { cufe: PropTypes.string }

const cellHead = {
  padding: "8px 10px", fontSize: 11, fontWeight: 600,
  color: "#475569", textTransform: "uppercase", letterSpacing: 0.4,
  borderBottom: "1px solid #e2e8f0", background: "#f1f5f9",
  position: "sticky", top: 0, zIndex: 1, whiteSpace: "nowrap",
}

const inputBase = { fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34 }
const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }

const LineasTab = ({ filtros }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(0)

  // Filtros propios del tab (extra a los del header)
  const [tipoComprobante, setTipoComprobante] = useState("")
  const [nitReceptor, setNitReceptor] = useState("")
  const [idProducto, setIdProducto] = useState("")
  const [orderBy, setOrderBy] = useState("fecha")
  const [orderDir, setOrderDir] = useState("desc")

  // Filtros aplicados (cambian solo al click "Aplicar")
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    tipo_comprobante: "", nit_receptor: "", id_producto: "",
    order_by: "fecha", order_dir: "desc",
  })

  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  const cargar = useCallback(async (pageIdx) => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      const { data: response } = await getDesgloseCompleto({
        ...filtros,
        ...filtrosAplicados,
        limit: PAGE_SIZE,
        offset: pageIdx * PAGE_SIZE,
      })
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el desglose.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros, filtrosAplicados])

  useEffect(() => {
    setPage(0)
    cargar(0)
  }, [cargar])

  const aplicarFiltros = () => {
    setFiltrosAplicados({
      tipo_comprobante: tipoComprobante,
      nit_receptor: nitReceptor.trim(),
      id_producto: idProducto.trim(),
      order_by: orderBy,
      order_dir: orderDir,
    })
  }

  const limpiarFiltros = () => {
    setTipoComprobante("")
    setNitReceptor("")
    setIdProducto("")
    setOrderBy("fecha")
    setOrderDir("desc")
    setFiltrosAplicados({ tipo_comprobante: "", nit_receptor: "", id_producto: "", order_by: "fecha", order_dir: "desc" })
  }

  const handleExportar = async (formato) => {
    if (exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarDesgloseExport({
        ...filtros,
        ...filtrosAplicados,
        formato,
      })
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 8000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const lineas = data?.lineas || []
  const meta = data?.metadata || {}
  const total = Number(meta.total_lineas) || lineas.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const hayFiltros = Boolean(filtrosAplicados.tipo_comprobante || filtrosAplicados.nit_receptor || filtrosAplicados.id_producto)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Banner explicativo */}
      <div style={{
        background: "#eff6ff", border: "1px solid #bfdbfe",
        color: "#1e40af", padding: "10px 14px", borderRadius: 8, fontSize: 13,
      }}>
        <i className="mdi mdi-magnify-scan me-1" />
        <strong>Auditoría línea por línea.</strong> Cada fila es UN producto en UNA factura. Útil para reportes contables,
        auditorías, reconciliación de devoluciones (NCs), o trazar qué le compró un cliente específico.
      </div>

      {/* Filtros propios del tab */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 16, border: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <div>
            <div style={labelStyle}><i className="mdi mdi-file-document me-1" />Tipo comprobante</div>
            <select style={{ ...inputBase, minWidth: 200 }} value={tipoComprobante} onChange={e => setTipoComprobante(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS_COMP.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}><i className="mdi mdi-account-search me-1" />NIT receptor</div>
            <input type="text" placeholder="Ej. 901100782"
              style={{ ...inputBase, minWidth: 180 }}
              value={nitReceptor}
              onChange={e => setNitReceptor(e.target.value)}
              onKeyDown={e => e.key === "Enter" && aplicarFiltros()}
            />
          </div>
          <div>
            <div style={labelStyle}><i className="mdi mdi-package-variant me-1" />ID producto</div>
            <input type="text" placeholder="Ej. 1030001"
              style={{ ...inputBase, minWidth: 160 }}
              value={idProducto}
              onChange={e => setIdProducto(e.target.value)}
              onKeyDown={e => e.key === "Enter" && aplicarFiltros()}
            />
          </div>
          <div>
            <div style={labelStyle}><i className="mdi mdi-sort me-1" />Ordenar por</div>
            <div className="d-flex gap-1">
              <select style={{ ...inputBase, minWidth: 120 }} value={orderBy} onChange={e => setOrderBy(e.target.value)}>
                {ORDER_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setOrderDir(orderDir === "desc" ? "asc" : "desc")}
                title={orderDir === "desc" ? "Mayor a menor" : "Menor a mayor"}>
                <i className={`mdi mdi-sort-${orderDir === "desc" ? "descending" : "ascending"}`} />
              </button>
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-sm"
            onClick={aplicarFiltros} disabled={loading}
            style={{ height: 34, padding: "0 16px", fontSize: 13, fontWeight: 600 }}>
            {loading ? <><Spinner size="sm" className="me-1" /> Aplicando…</> : <><i className="mdi mdi-magnify me-1" /> Aplicar</>}
          </button>
          {hayFiltros && (
            <button type="button" className="btn btn-outline-secondary btn-sm"
              onClick={limpiarFiltros} disabled={loading}
              style={{ height: 34, padding: "0 12px", fontSize: 13 }}>
              <i className="mdi mdi-close" /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {data && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <StatPill icon="mdi-format-list-bulleted" label="Líneas totales" value={formatNumber(meta.total_lineas)} accent="#6366f1" />
          <StatPill icon="mdi-receipt-text" label="Facturas" value={formatNumber(meta.total_facturas)} accent="#3b82f6" />
          <StatPill icon="mdi-package-variant" label="Productos únicos" value={formatNumber(meta.total_articulos_unicos)} accent="#8b5cf6" />
          <StatPill icon="mdi-package-variant-closed" label="Cantidad total" value={formatCantidad(meta.total_cantidad)} accent="#84cc16" />
          <StatPill icon="mdi-cash-multiple" label="Importe neto" value={formatCurrency(meta.total_importe_neto)} accent="#10b981"
            sub={meta.total_impuesto != null ? `${formatCurrency(meta.total_impuesto)} impuestos` : null} />
          <StatPill icon="mdi-account-group" label="Clientes únicos" value={formatNumber(meta.clientes_unicos)} accent="#06b6d4" />
          <StatPill icon="mdi-source-branch" label="Sucursales" value={formatNumber(meta.sucursales_unicas)} accent="#f59e0b" />
        </div>
      )}

      {/* Tabla */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 16, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-magnify-scan me-2 text-primary" />
              Desglose de líneas ({formatNumber(lineas.length)} en página · {formatNumber(total)} totales)
            </h6>
            {(meta.primera_fecha || meta.ultima_fecha) && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                {formatFecha(meta.primera_fecha)} → {formatFecha(meta.ultima_fecha)}
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span style={{ fontSize: 12, color: "#64748b" }}>Pág. {page + 1} / {totalPages}</span>
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-outline-success"
                onClick={() => handleExportar("xlsx")} disabled={!!exportando}>
                {exportando === "xlsx" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-excel me-1" /> Excel</>)}
              </button>
              <button type="button" className="btn btn-outline-primary"
                onClick={() => handleExportar("csv")} disabled={!!exportando}>
                {exportando === "csv" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-delimited me-1" /> CSV</>)}
              </button>
            </div>
          </div>
        </div>

        {(exportando === "xlsx" || exportando === "csv") && (
          <div style={{
            background: "#fffbeb", border: "1px solid #fde68a",
            color: "#92400e", padding: "8px 12px", borderRadius: 8,
            fontSize: 12, marginBottom: 10,
          }}>
            <i className="mdi mdi-clock-outline me-1" />
            Generando descarga (puede tardar varios segundos para volúmenes grandes — el back transmite en streaming).
          </div>
        )}

        {exportInfo && (
          <div style={{
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "8px 12px", borderRadius: 8,
            fontSize: 12, marginBottom: 10, display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="mdi mdi-check-circle" />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${formatNumber(exportInfo.totalRows)} líneas` : ""} · {exportInfo.filename}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 9 }} onClick={() => setExportInfo(null)} />
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8, marginBottom: 10 }}>
            <i className="mdi mdi-alert-circle me-1" />{error}
          </div>
        )}

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ maxHeight: 580, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={cellHead}>Fecha</th>
                  <th style={cellHead}>Folio</th>
                  <th style={cellHead}>Sucursal</th>
                  <th style={cellHead}>Receptor</th>
                  <th style={cellHead}>Producto</th>
                  <th style={{ ...cellHead, textAlign: "right" }}>Cant.</th>
                  <th style={{ ...cellHead, textAlign: "right" }}>P. unit.</th>
                  <th style={{ ...cellHead, textAlign: "right" }}>Importe</th>
                  <th style={{ ...cellHead, textAlign: "right" }}>Imp.</th>
                  <th style={{ ...cellHead, textAlign: "center" }}>Tasa</th>
                  <th style={{ ...cellHead, textAlign: "center" }}>CUFE</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr><td colSpan={11} style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
                    <Spinner color="primary" /> <div className="mt-2">Cargando…</div>
                  </td></tr>
                )}
                {!loading && lineas.length === 0 && (
                  <tr><td colSpan={11} style={{ padding: 30, textAlign: "center", color: "#64748b" }}>
                    Sin líneas para los filtros aplicados.
                  </td></tr>
                )}
                {lineas.map((l, i) => {
                  const recortado = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "—")
                  return (
                    <tr key={`${l.cufe}-${l.id_concepto || i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 10px", color: "#475569", whiteSpace: "nowrap" }}>{formatFecha(l.fecha)}</td>
                      <td style={{ padding: "6px 10px", color: "#0f172a", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {l.prefijo}-{l.folio}
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}>
                          {l.tipo_comprobante}
                        </div>
                      </td>
                      <td style={{ padding: "6px 10px", color: "#475569" }} title={l.sucursal}>
                        {recortado(l.sucursal, 22)}
                      </td>
                      <td style={{ padding: "6px 10px", color: "#475569" }} title={l.nombre_receptor}>
                        {recortado(l.nombre_receptor, 24)}
                        <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          {l.nit_receptor}
                        </div>
                      </td>
                      <td style={{ padding: "6px 10px", color: "#0f172a", fontWeight: 500 }} title={l.descripcion}>
                        {recortado(l.descripcion, 32)}
                        <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          ID: {l.id_producto}
                        </div>
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#475569" }}>
                        {formatCantidad(l.cantidad)}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#475569" }}>{formatCurrency(l.precio_unitario)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>{formatCurrency(l.importe_linea)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#f59e0b" }}>{formatCurrency(l.impuesto_linea)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "center", color: "#64748b" }}>
                        {l.tasa != null ? formatPercent(l.tasa, 0) : "—"}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "center" }}>
                        <CopiarCufeMini cufe={l.cufe} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { setPage(0); cargar(0) }}
              disabled={page === 0 || loading}>
              <i className="mdi mdi-page-first" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { const p = Math.max(0, page - 1); setPage(p); cargar(p) }}
              disabled={page === 0 || loading}>
              <i className="mdi mdi-chevron-left" /> Anterior
            </button>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {loading && <Spinner size="sm" className="me-2" />}
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {formatNumber(total)}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { const p = Math.min(totalPages - 1, page + 1); setPage(p); cargar(p) }}
              disabled={page + 1 >= totalPages || loading}>
              Siguiente <i className="mdi mdi-chevron-right" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { setPage(totalPages - 1); cargar(totalPages - 1) }}
              disabled={page + 1 >= totalPages || loading}>
              <i className="mdi mdi-page-last" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

LineasTab.propTypes = { filtros: PropTypes.object }

export default LineasTab
